import { faFacebook, faInstagram, faLinkedin, faTiktok, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { faGlobe } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React from "react";
import { Col, Row } from "react-bootstrap";
import { ResizeImage, safeJson } from "src/app/helpers/Index";
import {
	CLUB_SETTING_ADDRESS, CLUB_SETTING_BADGE, CLUB_SETTING_GROUND_NAME, CLUB_SETTING_DOMAIN, CLUB_SETTING_LEAGUE_IMAGE, CLUB_SETTING_NAME, CLUB_SETTING_PRIMARY_COLOUR, CLUB_SETTING_SECONDARY_COLOUR,
	CLUB_SETTING_SOCIAL_BLUESKY, CLUB_SETTING_SOCIAL_FACEBOOK, CLUB_SETTING_SOCIAL_INSTAGRAM,
	CLUB_SETTING_SOCIAL_LINKEDIN, CLUB_SETTING_SOCIAL_TIKTOK, CLUB_SETTING_SOCIAL_TWITTER,
	CLUB_SETTING_TEAMSHEET_SPONSOR_IMAGE, ClubSettingService
} from "src/app/services/ClubSettingService";
import { Dropdown } from "primereact/dropdown";
import { FileService, TeamService } from "src/app/services/Index";
import { ClubService } from "src/app/services/ClubService";
import { colours } from "src/app/Styles";
import { environment } from "src/environments/environment";
import { AdminScoreboardSettings } from "src/app/pages/admin/scoreboard-settings/AdminScoreboardSettings";

const SOCIALS = [
	{ icon: faFacebook, label: "Facebook", setting: CLUB_SETTING_SOCIAL_FACEBOOK },
	{ icon: faInstagram, label: "Instagram", setting: CLUB_SETTING_SOCIAL_INSTAGRAM },
	{ icon: faGlobe, label: "Bluesky", setting: CLUB_SETTING_SOCIAL_BLUESKY }, // FA 6.1 predates the Bluesky brand icon
	{ icon: faLinkedin, label: "LinkedIn", setting: CLUB_SETTING_SOCIAL_LINKEDIN },
	{ icon: faTwitter, label: "Twitter / X", setting: CLUB_SETTING_SOCIAL_TWITTER },
	{ icon: faTiktok, label: "TikTok", setting: CLUB_SETTING_SOCIAL_TIKTOK }
];

/**
 * Where each setting lives on the Club record. The club entity is the source
 * of truth; the old SystemSettings labels are kept as the UI's field keys so
 * the form (and the pre-multi-tenant fallback) stay unchanged.
 */
const CLUB_FIELDS: { [label: string]: string } = {
	[CLUB_SETTING_NAME]: "name",
	[CLUB_SETTING_ADDRESS]: "address",
	[CLUB_SETTING_GROUND_NAME]: "groundName",
	[CLUB_SETTING_DOMAIN]: "domain",
	[CLUB_SETTING_PRIMARY_COLOUR]: "primaryColour",
	[CLUB_SETTING_SECONDARY_COLOUR]: "secondaryColour",
	[CLUB_SETTING_BADGE]: "badgeUrl",
	[CLUB_SETTING_LEAGUE_IMAGE]: "leagueImageUrl",
	[CLUB_SETTING_TEAMSHEET_SPONSOR_IMAGE]: "teamsheetSponsorImageUrl",
	[CLUB_SETTING_SOCIAL_FACEBOOK]: "socialFacebook",
	[CLUB_SETTING_SOCIAL_INSTAGRAM]: "socialInstagram",
	[CLUB_SETTING_SOCIAL_BLUESKY]: "socialBluesky",
	[CLUB_SETTING_SOCIAL_LINKEDIN]: "socialLinkedin",
	[CLUB_SETTING_SOCIAL_TWITTER]: "socialTwitter",
	[CLUB_SETTING_SOCIAL_TIKTOK]: "socialTiktok"
};

type Props = {};
type State = {
	activeTab: string;
	club: any;
	isSaving: boolean;
	teams: any[];
	values: { [label: string]: string };
};

export class AdminClubSettings extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { activeTab: "identity", club: null, isSaving: false, teams: [], values: {} };
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		// Multi-tenant Club entity is the source of truth when present; the
		// SystemSettings map is only read as a fallback for older databases.
		let club: any = null;
		try {
			const clubRes = await ClubService.getMy();
			if (clubRes.ok) {
				const loaded = await clubRes.json();
				if (loaded && loaded.id) {
					club = loaded;
					const teamsRes = await safeJson(TeamService.getAll());
					this.setState({ club, teams: Array.isArray(teamsRes) ? teamsRes : [] });
				}
			}
		} catch { /* pre-multi-tenant API */ }

		if (club) {
			const values: { [label: string]: string } = {};
			for (const [label, field] of Object.entries(CLUB_FIELDS)) values[label] = club[field] || "";
			this.setState({ values });
			return;
		}

		this.setState({ values: await ClubSettingService.getMap() });
	}

	setValue(label: string, value: string) {
		this.setState({ values: { ...this.state.values, [label]: value } });
	}

	value(label: string) {
		return this.state.values[label] || "";
	}

	async uploadImage(data: any, label: string) {
		const uploadData = new FormData();
		uploadData.append("containerName", "uploads");
		uploadData.append("files", await ResizeImage(data.files[0], 1600));
		uploadData.append("mimeTypeKey", "Images");
		const res = await FileService.upload(uploadData);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Upload failed - check the file type.", life: 5000, severity: "error", summary: "Upload failed" });
			return;
		}
		const response = await res.json();
		if (Array.isArray(response) && response[0]?.location) {
			this.setValue(label, `${environment.server.storageUrl}/${response[0].location}`);
		}
	}

	async save() {
		this.setState({ isSaving: true });
		let ok: boolean;

		if (this.state.club?.id) {
			// Everything lives on the club record — one PATCH, no SystemSettings.
			const club = { ...this.state.club };
			for (const [label, field] of Object.entries(CLUB_FIELDS)) club[field] = this.value(label);
			const clubRes = await ClubService.updateSingleById(club.id, club);
			ok = clubRes.ok;
			if (ok) this.setState({ club });
		} else {
			// Pre-multi-tenant API: fall back to the SystemSettings store.
			const values: { [label: string]: string } = {};
			for (const label of Object.keys(CLUB_FIELDS)) values[label] = this.value(label);
			ok = await ClubSettingService.setMany(values);
		}

		this.setState({ isSaving: false });
		this.toast.show(ok
			? { closable: false, detail: "Club settings saved.", life: 3000, severity: "success", summary: "Saved" }
			: { closable: false, detail: "Some settings failed to save - try again.", life: 5000, severity: "error", summary: "Something went wrong!" });
		if (ok) this.load();
	}

	renderImageField(label: string, setting: string, hint: string) {
		const url = this.value(setting);
		return (
			<div className="field" style={{ marginBottom: 16 }}>
				<label style={{ width: "100%" }}>{label}</label>
				{url
					? <img alt={label} src={url} style={{ background: "#FFFFFF", borderRadius: 10, display: "block", marginBottom: 10, marginTop: 8, maxHeight: 90, maxWidth: "100%", padding: 6 }} />
					: <p style={{ color: "var(--pb-text-muted)", marginBottom: 8 }}>{hint}</p>
				}
				<FileUpload accept="image/*" auto customUpload mode="basic" name={setting} uploadHandler={(data) => this.uploadImage(data, setting)}></FileUpload>
			</div>
		);
	}

	renderSaveButton() {
		return <Button icon="pi pi-check" label="Save Club Settings" loading={this.state.isSaving} style={{ backgroundColor: colours.buttons.add, borderColor: colours.buttons.add, marginTop: 4 }} onClick={() => this.save()}></Button>;
	}

	render() {
		return (
			<>
				<div className="card" style={{ padding: "1.5rem" }}>
					<h5 style={{ marginBottom: "0.5rem" }}>Club Settings</h5>
					<div className="pb-tabs">
						{[
							{ icon: "pi pi-id-card", key: "identity", label: "Club Identity" },
							{ icon: "pi pi-share-alt", key: "socials", label: "Socials" },
							{ icon: "pi pi-file", key: "teamsheet", label: "Teamsheet" },
							{ icon: "pi pi-desktop", key: "scoreboard", label: "Scoreboard" }
						].map((tab) =>
							<button className={`pb-tab${this.state.activeTab === tab.key ? " active" : ""}`} key={tab.key} onClick={() => this.setState({ activeTab: tab.key })} type="button">
								<i className={tab.icon} /> {tab.label}
							</button>
						)}
					</div>
					{this.state.activeTab === "identity" && <div className="pb-tab-panel">
							<div className="field" style={{ marginBottom: 12 }}>
								<label htmlFor="clubName">Club Name</label>
								<InputText id="clubName" onChange={(e) => this.setValue(CLUB_SETTING_NAME, e.target.value)} placeholder="Scunthorpe United" style={{ width: "100%" }} value={this.value(CLUB_SETTING_NAME)} />
							</div>
							<div className="field" style={{ marginBottom: 12 }}>
								<label htmlFor="clubAddress">Club Address</label>
								<InputText id="clubAddress" onChange={(e) => this.setValue(CLUB_SETTING_ADDRESS, e.target.value)} placeholder="Attis Arena, Glanford Park, Scunthorpe" style={{ width: "100%" }} value={this.value(CLUB_SETTING_ADDRESS)} />
							</div>
							<div className="field" style={{ marginBottom: 12 }}>
								<label htmlFor="clubGroundName">Ground Name</label>
								<InputText id="clubGroundName" onChange={(e) => this.setValue(CLUB_SETTING_GROUND_NAME, e.target.value)} placeholder="Glanford Park" style={{ width: "100%" }} value={this.value(CLUB_SETTING_GROUND_NAME)} />
								<small style={{ color: "#6b7280" }}>Shown on the scoreboard&rsquo;s &ldquo;Next Up at &hellip;&rdquo; slide.</small>
							</div>
							<div className="field" style={{ marginBottom: 12 }}>
								<label htmlFor="clubDomain">Club Domain</label>
								<InputText id="clubDomain" onChange={(e) => this.setValue(CLUB_SETTING_DOMAIN, e.target.value)} placeholder="scunthorpe-united.co.uk" style={{ width: "100%" }} value={this.value(CLUB_SETTING_DOMAIN)} />
							</div>
							<Row>
								<Col>
									<div className="field" style={{ marginBottom: 12 }}>
										<label htmlFor="clubColour">Primary Colour</label>
										<div style={{ alignItems: "center", display: "flex", gap: 10 }}>
											<input id="clubColour" onChange={(e) => this.setValue(CLUB_SETTING_PRIMARY_COLOUR, e.target.value)} style={{ background: "none", border: "none", cursor: "pointer", height: 42, padding: 0, width: 56 }} type="color" value={this.value(CLUB_SETTING_PRIMARY_COLOUR) || "#A83249"} />
											<InputText onChange={(e) => this.setValue(CLUB_SETTING_PRIMARY_COLOUR, e.target.value)} placeholder="#A83249" style={{ width: 120 }} value={this.value(CLUB_SETTING_PRIMARY_COLOUR)} />
										</div>
									</div>
								</Col>
								<Col>
									<div className="field" style={{ marginBottom: 12 }}>
										<label htmlFor="clubColour2">Secondary Colour</label>
										<div style={{ alignItems: "center", display: "flex", gap: 10 }}>
											<input id="clubColour2" onChange={(e) => this.setValue(CLUB_SETTING_SECONDARY_COLOUR, e.target.value)} style={{ background: "none", border: "none", cursor: "pointer", height: 42, padding: 0, width: 56 }} type="color" value={this.value(CLUB_SETTING_SECONDARY_COLOUR) || "#87CEEB"} />
											<InputText onChange={(e) => this.setValue(CLUB_SETTING_SECONDARY_COLOUR, e.target.value)} placeholder="#87CEEB" style={{ width: 120 }} value={this.value(CLUB_SETTING_SECONDARY_COLOUR)} />
										</div>
									</div>
								</Col>
							</Row>
							{this.state.club?.id &&
								<div className="field" style={{ marginBottom: 12 }}>
									<label htmlFor="homeTeam">Home Team</label>
									<Dropdown id="homeTeam" onChange={(e: any) => this.setState({ club: { ...this.state.club, homeTeamId: e.value } })} options={this.state.teams.map((team) => ({ label: team.teamName, value: team.id }))} placeholder="Select your club's team" style={{ width: "100%" }} value={this.state.club.homeTeamId} />
									<small style={{ color: "var(--pb-text-muted)", fontWeight: 600 }}>Drives the scoreboard home side, dashboards, and the Remote's Home column.</small>
								</div>
							}
							{this.renderImageField("Club Badge", CLUB_SETTING_BADGE, "No badge uploaded - club outputs use the bundled badge asset.")}
							{this.renderSaveButton()}
					</div>}
					{this.state.activeTab === "socials" && <div className="pb-tab-panel">
							{SOCIALS.map((social) =>
								<div className="field" key={social.setting} style={{ alignItems: "center", display: "flex", gap: 10, marginBottom: 10 }}>
									<span style={{ color: "var(--pb-text-muted)", fontSize: 18, textAlign: "center", width: 26 }}><FontAwesomeIcon icon={social.icon} /></span>
									<InputText onChange={(e) => this.setValue(social.setting, e.target.value)} placeholder={`${social.label} link or handle`} style={{ flex: 1 }} value={this.value(social.setting)} />
								</div>
							)}
							{this.renderSaveButton()}
					</div>}
					{this.state.activeTab === "teamsheet" && <div className="pb-tab-panel">
							<p style={{ color: "var(--pb-text-muted)", fontWeight: 600 }}>Used on the printed A4 teamsheet.</p>
							{this.renderImageField("League Image (teamsheet header)", CLUB_SETTING_LEAGUE_IMAGE, "No league image set - the header shows the date only.")}
							{this.renderImageField("Official Teamsheet Sponsor (footer)", CLUB_SETTING_TEAMSHEET_SPONSOR_IMAGE, "No sponsor image set - the footer falls back to the match sponsor's name.")}
							{this.renderSaveButton()}
					</div>}
					{this.state.activeTab === "scoreboard" && <div className="pb-tab-panel">
							<AdminScoreboardSettings />
					</div>}
				</div>
				<Toast ref={(el) => this.toast = el}></Toast>
			</>
		);
	}
}
